import * as _ from 'underscore';
import { urlUtil, util } from '../util';
import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';
import { IObject } from '../../../type_definitions/datatypes';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { GaMessageService } from '../message/ga-message.service';

interface IHttpOptions {
  body?:any;
  headers?:HttpHeaders | {
    [header: string]: string | string[];
  };
  observe?:'body';
  params?:HttpParams | {
    [param: string]:string | string[];
  };
  responseType?:'json';
  reportProgress?:boolean;
  withCredentials?:boolean;
  noIfMatch?:boolean;
}

export interface IHttpConfig {
  method:string;
  url:string;
  options?:IHttpOptions;
}

export interface IHttpDataMessage<T> {
  status:string;
  data?:T;
  message?:string;
}

export enum HttpError {
  REQUEST_CANCELLED
}

export enum HttpResponseStatus {
  OK = 'OK',
  FAIL = 'FAIL'
}

export interface IHttpDataCache<T> {
  clear():void;
  remove(url:string, params:any):void;
  doGET(url:string, params?:any, loadingMessage?:string, errorMessage?:string, cancelId?:any):Promise<T>;
  isLoadingDone():Promise<boolean>;
  loading():boolean;
  [ others:string ]:any; //to allow for extra methods on the implementing object
}

interface IHttpRunningRequest<T> {
  [key:string]:Array<T>;
}

/**
 * @ngdoc service
 * @name ga2.shared.services.http:httpDataService
 *
 * @description A service for changing responses of type {data: {status: 'FAIL'}} to HTTP errors
 *
 * @require httpClient
 * @require injector
 * @require ga2.shared.services.http:this.deferredService
 */
@Injectable()
export class HttpDataService {
  private runningRequests:IHttpRunningRequest<Subscription> = {};
  private runningDeferredRequests:IHttpRunningRequest<IGADeferred<any>> = {};
  private config:IHttpConfig;

  constructor(
    private httpClient:HttpClient,
    private deferredService:GaDeferredService,
    private injector:Injector
  ) {
    /**/
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doHTTP
   * @methodOf ga2.shared.services.http:httpDataService
   * @description
   * @param {object} config - contains parameters for doing http request
   * @param {string} busyMessage - what to show on success
   * @param {string} errorMessage - what to show on fail
   * @param {string} cancelId - the id to identify this request if it can be cancelled by a similar request
   * @param {boolean} returnDataOnFail
   */
  public doHTTP<T>(config:IHttpConfig, busyMessage?:string, errorMessage?:string, cancelId?:string, returnDataOnFail?:boolean):Promise<T> {
    let messageService:GaMessageService;

    if (busyMessage || errorMessage) {
      messageService = this.injector.get(GaMessageService);
    }

    const msg:Noty = busyMessage? messageService.showStatusMessage(busyMessage) : undefined;
    const deferred:IGADeferred<any> = this.deferredService.defer();

    this.config = config;

    const httpSuccess = (response:IHttpDataMessage<T>):T => {
      const responseData:any = response?.data || <any>response;
      deferred.resolve(responseData);
      return responseData;
    };

    const httpError = (response:HttpErrorResponse):Observable<never> => {
      // status 0 means we cancelled the request ourselves
      if (response.status && Number(response.status) !== HttpError.REQUEST_CANCELLED) {
        if (errorMessage) {
          messageService.showResponseErrorMessage(errorMessage, (response.error?.status || response.status), response.error);
        }
      }

      deferred.reject(returnDataOnFail ? response.error : (response.error?.message || response.status));

      return EMPTY;
    };

    const httpDone = ():void => {
      const index:number = _.indexOf(this.runningRequests[cancelId], runningRequest);

      if (!!~index) {
        this.runningRequests[cancelId].splice(index, 1);
        this.runningDeferredRequests[cancelId].splice(index, 1);
      }

      if (msg) {
        messageService.closeMessage(msg);
      }
    };

    if (config.options?.params) {
      // remove undefined object property value
      config.options.params = util.deepClone(config.options.params);
    }

    const runningRequest:Subscription = this.httpClient.request<IHttpDataMessage<T>>(config.method, config.url, config.options)
      .pipe(
        catchError(httpError),
        map((response:IHttpDataMessage<T>) => {
          // check whether request is cancelled
          if (cancelId && runningRequest.closed) {
            deferred.reject({ status: HttpError.REQUEST_CANCELLED});
            return response;
          }

          return httpSuccess(response);
        }),
        finalize(httpDone)
      )
      .subscribe();

    if (cancelId) {
      this.cancelRequests(cancelId);
      this.addRunningRequests(cancelId, runningRequest, deferred);
    }

    return deferred.promise
      .finally(httpDone);
  }

  public addRunningRequests(cancelId:string, runningRequest:Subscription, runningDeferredRequest:IGADeferred<any>) {
    this.runningRequests[cancelId] = [];
    this.runningRequests[cancelId].push(runningRequest);

    // deferred
    this.runningDeferredRequests[cancelId] = [];
    this.runningDeferredRequests[cancelId].push(runningDeferredRequest);
  }

  public cancelRequests(cancelId:string):void {
    while (this.runningRequests[ cancelId ] && this.runningRequests[ cancelId ].length) {
      const runningRequest:Subscription = this.runningRequests[ cancelId ].shift();
      const runningDeferredRequest:IGADeferred<any> = this.runningDeferredRequests[cancelId].shift();
      console.debug('canceled running load: ' + cancelId);
      runningRequest.unsubscribe();
      runningDeferredRequest.reject({ status: HttpError.REQUEST_CANCELLED});
    }
  }

  public cancelAllRequests(cancelIdMatch?:string):void {
    Object.keys(this.runningRequests).forEach((cancelId:string) => {
      if (cancelId.includes(cancelIdMatch || '')) {
        this.cancelRequests(cancelId);
      }
    });
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doGET
   * @methodOf ga2.shared.services.http:httpDataService
   * @description get request
   * @param {string} url - where to send to?
   * @param {object=} params - contains url params
   * @param {string=} loadingMessage - what to show while loading
   * @param {string=} errorMessage - what to show on fail
   * @param {string=} cancelId - the id to identify this request if it can be cancelled by a similar request
   */
  public doGET<T>(url:string, params?:IObject, loadingMessage?:string, errorMessage?:string, cancelId?:any):Promise<T> {
    const config:IHttpConfig = {
      method: 'GET',
      url,
      options: {
        params
      }
    };
    return this.doHTTP<T>(config, loadingMessage, errorMessage, cancelId);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doPATCH
   * @methodOf ga2.shared.services.http:httpDataService
   * @description patch request
   * @param {string} url - where to send to?
   * @param {object} params - contains url params
   * @param {any} body - which data to send?
   * @param {string} loadingMessage - what to show while loading
   * @param {string} errorMessage - what to show on fail
   * @param {string=} cancelId - the id to identify this request if it can be cancelled by a similar request
   */
  public doPATCH<T>(url:string, params:IObject, body:any, loadingMessage:string, errorMessage:string, cancelId?:any):Promise<T> {
    const config:IHttpConfig = {
      method: 'PATCH',
      url,
      options: {
        params,
        body
      }
    };
    return this.doHTTP<T>(config, loadingMessage, errorMessage, cancelId);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doPUT
   * @methodOf ga2.shared.services.http:httpDataService
   * @description put request
   * @param {string} url - where to send to?
   * @param {object} params - contains url params
   * @param {body} body - which data to send?
   * @param {string} loadingMessage - what to show while loading
   * @param {string} errorMessage - what to show on fail
   * @param {string=} cancelId - the id to identify this request if it can be cancelled by a similar request
   */
  public doPUT<T>(url:string, params:any, body:any, loadingMessage:string, errorMessage:string, cancelId?:any):Promise<T> {
    const config:IHttpConfig = {
      method: 'PUT',
      url,
      options: {
        params,
        body
      }
    };
    return this.doHTTP<T>(config, loadingMessage, errorMessage, cancelId);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doPOST
   * @methodOf ga2.shared.services.http:httpDataService
   * @description post request
   * @param {string} url - where to send to?
   * @param {object} params - contains url params
   * @param {any | FormData} body - which data to send?
   * @param {string} loadingMessage - what to show while loading
   * @param {string} errorMessage - what to show on fail
   * @param {string=} cancelId - the id to identify this request if it can be cancelled by a similar request
   * @param {boolean} returnDataOnFail
   */
  public doPOST<T>(url:string, params:IObject, body:any | FormData, loadingMessage?:string, errorMessage?:string, cancelId?:any, returnDataOnFail?:boolean):Promise<T> {
    const config:IHttpConfig = {
      method: 'POST',
      url: url,
      options: {
        params,
        body
      }
    };
    return this.doHTTP<T>(config, loadingMessage, errorMessage, cancelId, returnDataOnFail);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services.http:httpDataService#doDELETE
   * @methodOf ga2.shared.services.http:httpDataService
   * @description DELETE request
   * @param {string} url - where to send to?
   * @param {object} params - contains url params
   * @param {string=} deletingMessage - what to show while deleting
   * @param {string=} errorMessage - what to show when delete fails
   * @param {string=} cancelId - the id to identify this request if it can be cancelled by a similar request
   */
  public doDELETE<T>(url:string, params:IObject, deletingMessage?:string, errorMessage?:string, cancelId?:any):Promise<T> {
    const config:IHttpConfig = {
      method: 'DELETE',
      url: url,
      options: {
        params
      }
    };
    return this.doHTTP<T>(config, deletingMessage, errorMessage, cancelId);
  }

  public createCache<T>(delay?:number):IHttpDataCache<T> {
    const httpDataService = this;
    return {
      cache: {},
      callsLoading: {},
      stillLoading: this.deferredService.defer(),
      clear: clear,
      evaluateLoading: evaluateLoading,
      loading: loading,
      isLoadingDone: isLoadingDone,
      remove: remove,
      doGET: doGET
    };

    function clear():void {
      this.cache = {};
    }

    function evaluateLoading():void {
      if (!this.loading()) {
        this.stillLoading.resolve();
      }
    }

    function loading():boolean {
      return !!_.find(this.callsLoading, (value) => {
        return value;
      }); //any property that is truthy -> still loading
    }

    function isLoadingDone():Promise<boolean> {
      this.evaluateLoading();
      return this.stillLoading.promise;
    }

    function remove(url:string, params:any) {
      const key:string = urlUtil.setQueryParams(url, params);
      this.cache[ key ] = undefined;
    }

    function doGET(url:string, params:any, loadingMessage:string, errorMessage:string, cancelId?:any):Promise<T> {
      const key:string = urlUtil.setQueryParams(url, params);
      if (this.cache[ key ]) {
        if (cancelId) {
          httpDataService.cancelRequests(cancelId);
        }

        if (delay) {
          const deferred:IGADeferred<any> = httpDataService.deferredService.defer();
          setTimeout(():void => {
            deferred.resolve(this.cache[ key ]);
          }, delay, true);
          return deferred.promise;
        } else {
          return Promise.resolve(this.cache[ key ]);
        }
      }

      this.callsLoading[ key ] = true;
      const c:IHttpDataCache<T> = this;
      return httpDataService.doGET(url, params, loadingMessage, errorMessage, cancelId)
        .then((data:any) => {
          c.callsLoading[ key ] = false;
          c.evaluateLoading();
          c.cache[ key ] = data;
          return <T>data;
        });
    }
  }

  /**
   * @ngdoc method
   * @name ga2.common.service:httpDataService#config
   * @methodOf ga2.common.service:httpDataService
   * @description Get the request configuration
   */
  public getConfig():IHttpConfig {
    return this.config;
  }
}